// src/components/RotationPlannerView.js
import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import PlotCard from "./PlotCard";
import apiClient from "../utils/apiClient";
import "../style/RotationPlanner.css";

const RotationPlannerView = ({ 
  plots, 
  plantedCrops,
  garden_uuid, 
  year, 
  plotGroups = [],
  initialPlotGroup = '',
  onNavigate, 
  onPlanCrop, 
  onAssignFruit, 
  onManageCompost,
  onEditPlanted,
  onPlantedUpdated
}) => {
  const [selectedPlotGroup, setSelectedPlotGroup] = useState(initialPlotGroup);
  const [rotationRules, setRotationRules] = useState({}); // crop_uuid -> { group_number, group_name }

  // Get the selected plot group details
  const selectedPlotGroupData = plotGroups.find(pg => pg.plot_group_uuid === selectedPlotGroup);

  // Update selectedPlotGroup when initialPlotGroup changes (from URL)
  useEffect(() => {
    if (initialPlotGroup !== selectedPlotGroup) {
      setSelectedPlotGroup(initialPlotGroup);
    }
  }, [initialPlotGroup]);

  // Auto-select first plot group if none is selected and plot groups are available
  useEffect(() => {
    if (!selectedPlotGroup && plotGroups.length > 0 && !initialPlotGroup) {
      const firstPlotGroup = plotGroups[0].plot_group_uuid;
      setSelectedPlotGroup(firstPlotGroup);
      // Update URL with the first plot group
      onNavigate(`/gardens/${garden_uuid}/rotation/${year}/${firstPlotGroup}`);
    }
  }, [selectedPlotGroup, plotGroups, initialPlotGroup, garden_uuid, year, onNavigate]);

  // Fetch rotation rules when plot group changes
  useEffect(() => {
    const fetchRotationRules = async () => {
      if (!selectedPlotGroup || !selectedPlotGroupData) {
        setRotationRules({});
        return;
      }

      try {
        const response = await apiClient.get(
          `/rotation-family-rules/${selectedPlotGroupData.rotation_type}`
        );
        
        // Create a map of crop_uuid -> { group_number, group_name }
        const rulesMap = {};
        if (response.data.groups) {
          response.data.groups.forEach((group) => {
            if (group.crops) {
              group.crops.forEach((crop) => {
                rulesMap[crop.crop_uuid] = {
                  group_number: group.group_number,
                  group_name: group.group_name,
                };
              });
            }
          });
        }
        setRotationRules(rulesMap);
      } catch (err) {
        console.error("Error fetching rotation rules:", err);
        setRotationRules({});
      }
    };

    fetchRotationRules();
  }, [selectedPlotGroup, selectedPlotGroupData]);

  const getRotationTypeLabel = (rotationType) => {
    switch (rotationType) {
      case 'three_year':
        return '3 Year';
      case 'four_year':
        return '4 Year';
      case 'six_year':
        return '6 Year';
      default:
        return rotationType;
    }
  };

  // Get number of years from rotation type
  const getRotationYears = (rotationType) => {
    switch (rotationType) {
      case 'three_year':
        return 3;
      case 'four_year':
        return 4;
      case 'six_year':
        return 6;
      default:
        return 3;
    }
  };

  // Generate array of years for the rotation cycle
  const rotationYears = useMemo(() => {
    if (!selectedPlotGroupData) return [];
    const numYears = getRotationYears(selectedPlotGroupData.rotation_type);
    return Array.from({ length: numYears }, (_, i) => year + i);
  }, [selectedPlotGroupData, year]);

  // Get plots in the selected plot group, sorted by name
  const plotsInGroup = useMemo(() => {
    if (!selectedPlotGroup) return [];
    const filtered = plots.filter(plot => 
      plot.plot_type?.name?.toLowerCase() !== 'visual element' &&
      plot.type !== "Building" &&
      plot.plot_group_uuid === selectedPlotGroup
    );
    // Sort by name in ascending order (A, B, C, D, E, F)
    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [plots, selectedPlotGroup]);

  // Get crops by plot for a specific year
  const getCropsByPlotForYear = (targetYear) => {
    const filteredPlantedCrops = plantedCrops.filter(
      (planted) =>
        planted.date_planted &&
        new Date(planted.date_planted).getFullYear() === targetYear
    );

    return plotsInGroup.map((plot) => {
      const plotCrops = filteredPlantedCrops.filter(
        (p) => p.plot_uuid === plot.plot_uuid
      );
      
      // Get unique group names for crops in this plot
      const groupNames = new Set();
      plotCrops.forEach((planted) => {
        if (planted.crop?.crop_uuid && rotationRules[planted.crop.crop_uuid]) {
          groupNames.add(rotationRules[planted.crop.crop_uuid].group_name);
        }
      });
      
      return {
        plot,
        crops: plotCrops,
        groupNames: Array.from(groupNames).sort(),
      };
    });
  };

  const handleAssignFruitFromCard = (plot) => {
    // Call the parent's onAssignFruit with the specific plot
    onAssignFruit(plot);
  };

  const handlePlanCropFromCard = (plot, rotationYear) => {
    // Call the parent's onPlanCrop with the specific plot, year, and plot group
    if (onPlanCrop) {
      onPlanCrop(plot, rotationYear, selectedPlotGroup);
    }
  };

  const handleCoverCropAdded = () => {
    if (onPlantedUpdated) {
      onPlantedUpdated();
    }
  };

  const handlePrint = () => {
    if (!selectedPlotGroupData) return;

    // Create a print window with only the rotation view content
    const printWindow = window.open('', '_blank');
    const printContent = document.querySelector('.rotation-planner-view');
    
    if (!printContent) return;

    // Clone the content
    const clonedContent = printContent.cloneNode(true);
    
    // Remove buttons and form controls from cloned content
    const buttons = clonedContent.querySelectorAll('button, .MuiButton-root, .MuiIconButton-root, .MuiFormControl-root');
    buttons.forEach(btn => btn.remove());

    // Create print HTML
    const printHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Rotation Plan - ${selectedPlotGroupData.name}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              background: white;
              color: black;
            }
            .plot-group-settings-card {
              background: white;
              border: 1px solid #000;
              padding: 1rem;
              margin-bottom: 1rem;
              page-break-inside: avoid;
            }
            .plot-group-settings-card h6 {
              font-size: 1.2rem;
              font-weight: 600;
              margin-bottom: 0.5rem;
              color: #000;
            }
            .rotation-year-header {
              font-size: 1.5rem;
              font-weight: 600;
              border-bottom: 2px solid #000;
              padding-bottom: 0.5rem;
              margin-top: 1.5rem;
              margin-bottom: 1rem;
              page-break-after: avoid;
              color: #000;
            }
            .rotation-planner-grid {
              display: grid;
              grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
              gap: 1rem;
              margin-bottom: 2rem;
              page-break-inside: avoid;
            }
            .plot-card-container {
              page-break-inside: avoid;
            }
            .MuiPaper-root {
              background: white !important;
              border: 1px solid #000 !important;
              box-shadow: none !important;
              padding: 0.5rem !important;
              margin-bottom: 0.5rem;
            }
            .MuiCardHeader-root {
              padding: 0.25rem 0.5rem !important;
              border-bottom: 1px solid #ccc;
            }
            .MuiCardContent-root {
              padding: 0.25rem 0.5rem !important;
            }
            .MuiTable-root {
              width: 100%;
            }
            .MuiTableCell-root {
              padding: 4px 0 !important;
              border: none !important;
              font-size: 0.9rem;
            }
            @media print {
              body {
                padding: 10px;
              }
              .rotation-year-header {
                page-break-after: avoid;
              }
              .plot-card-container {
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          ${clonedContent.innerHTML}
        </body>
      </html>
    `;

    printWindow.document.write(printHTML);
    printWindow.document.close();
    
    // Wait for content to load, then print
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
        // Optionally close after printing
        // printWindow.close();
      }, 250);
    };
  };

  return (
    <Box className="rotation-planner-view">
      {/* Plot Group Filter Dropdown and Print Button */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="plot-group-filter-label">Plot Group</InputLabel>
          <Select
            labelId="plot-group-filter-label"
            id="plot-group-filter"
            value={selectedPlotGroup}
            label="Plot Group"
            onChange={(e) => {
              const newPlotGroup = e.target.value;
              setSelectedPlotGroup(newPlotGroup);
              // Update URL with new plot group
              onNavigate(`/gardens/${garden_uuid}/rotation/${year}/${newPlotGroup || ''}`);
            }}
          >
            {plotGroups.length === 0 ? (
              <MenuItem value="" disabled>
                No plot groups available
              </MenuItem>
            ) : (
              plotGroups.map((group) => (
                <MenuItem key={group.plot_group_uuid} value={group.plot_group_uuid}>
                  {group.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          className="vintage-button rotation-planner-print-button"
          disabled={!selectedPlotGroupData}
        >
          Print
        </Button>
      </Box>

      {/* Plot Group Settings Card */}
      {selectedPlotGroupData && (
        <Box className="plot-group-settings-card">
          <Typography variant="h6" gutterBottom>
            {selectedPlotGroupData.name}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            <strong>Rotation Type:</strong> {getRotationTypeLabel(selectedPlotGroupData.rotation_type)}
          </Typography>
        </Box>
      )}

      {/* Rotation Years Grid */}
      {!selectedPlotGroup ? (
        <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
          Please select a plot group to view rotation planning.
        </Typography>
      ) : plotsInGroup.length === 0 ? (
        <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
          No plots found in this plot group.
        </Typography>
      ) : (
        <Box>
          {rotationYears.map((rotationYear) => {
            const cropsByPlotForYear = getCropsByPlotForYear(rotationYear);
            return (
              <Box key={rotationYear} sx={{ mb: 4 }}>
                {/* Year Header */}
                <Typography 
                  variant="h5" 
                  className="rotation-year-header"
                  sx={{ mb: 2 }}
                >
                  {rotationYear}
                </Typography>
                
                {/* Plot Cards Grid for this year */}
                <Box className="rotation-planner-grid">
                  {cropsByPlotForYear.map(({ plot, crops, groupNames }) => (
                    <Box key={plot.plot_uuid} className="plot-card-container">
                      <PlotCard
                        plot={plot}
                        crops={crops}
                        year={rotationYear}
                        simplified={true}
                        rotationGroupNames={groupNames}
                        onPlantedUpdated={handleCoverCropAdded}
                        onClick={() =>
                          onNavigate(
                            `/gardens/${garden_uuid}/plots/${plot.plot_uuid}/detail/${rotationYear}`
                          )
                        }
                        onAssignFruit={plot.plot_type?.name?.toLowerCase() === 'fruit tree' ? handleAssignFruitFromCard : null}
                        onPlanCrop={plot.plot_type?.name?.toLowerCase() === 'vegetable bed' ? (plot) => handlePlanCropFromCard(plot, rotationYear) : null}
                        onEditPlanted={onEditPlanted}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default RotationPlannerView;

